/**
 * Shared UI primitives used by EntityDetailModal.
 * Extracting these dropped EntityDetailModal's local complexity substantially
 * (was C=91) without changing any behaviour.
 */
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Collapsible section with an icon header. Content only mounts when expanded
 * to keep the modal cheap on large entities.
 */
export function ExpandableSection({ title, icon: Icon, isExpanded, onToggle, children }) {
  const slug = String(title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`entity-section-${slug}`}>
      <button
        onClick={onToggle}
        data-testid={`entity-section-toggle-${slug}`}
        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon ? <Icon className="w-5 h-5 text-slate-600" /> : null}
          <span className="font-medium text-slate-900">{title}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isExpanded && <div className="p-4 bg-white" data-testid={`entity-section-content-${slug}`}>{children}</div>}
    </div>
  );
}

/**
 * Render a plain (possibly object) list as an <ul>. Objects are stringified.
 * Empty lists render an italic "Not available" placeholder.
 */
export function EntityList({ items, emptyText = 'Not available' }) {
  if (!items || items.length === 0) {
    return <span className="text-slate-400 italic">{emptyText}</span>;
  }
  return (
    <ul className="space-y-1">
      {items.map((item, idx) => {
        const rendered = typeof item === 'object' ? JSON.stringify(item) : item;
        return (
          <li key={`${rendered}-${idx}`} className="text-slate-700">
            {rendered}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Label + value row. Renders nothing when value is empty/null/undefined,
 * so callers can dump every field unconditionally.
 */
export function EntityField({ label, value }) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  const displayValue = Array.isArray(value) ? value.join(', ') : value;
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm font-medium text-slate-500 sm:w-32 shrink-0">{label}</span>
      <span className="text-sm text-slate-900">{displayValue}</span>
    </div>
  );
}
